// libraries
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Dinero from 'dinero.js'

// queries and mutations
import { useGetMaterialTypesQuery } from 'services/materialTypes'

// helpers and utils
import { DEFAULT_PRECISION } from 'utils/constants'
import { getNetValue } from 'utils/data-manipulation/common'
import { roundTo } from 'utils/data-manipulation/valueFormatter'

const useUpdateScaleIBTPrices = ({
  supplementalPriceSheetIds,
  isDirty,
  inboundTicketMaterials,
  setInboundTicketMaterials,
}) => {
  const { defaultCurrency, defaultYardUm } = useSelector(state => state.settings)

  const materialTypeIds = useMemo(() => {
    const set = new Set()
    inboundTicketMaterials.forEach(material => {
      if (material.materialTypeId) {
        set.add(material.materialTypeId)
      }
    })
    return Array.from(set)
  }, [supplementalPriceSheetIds])

  const { data: materialTypesData, isFetching } = useGetMaterialTypesQuery(
    { filter: JSON.stringify({ materialTypeIds, supplementalPriceSheetIds }) },
    { skip: materialTypeIds.length === 0 },
  )

  const materialTypesDict = useMemo(() => {
    const newDict = {}
    materialTypesData?.Items?.forEach(materialType => {
      newDict[materialType.id] = materialType
    })
    return newDict
  }, [materialTypesData])

  useEffect(() => {
    if (!isDirty) return // skip on initial page load
    if (!materialTypesData) return

    const formattedMaterials = inboundTicketMaterials.map(material => {
      const gross = Number(material?.weightAndPrice?.gross?.amount || 0)
      const tare = Number(material?.weightAndPrice?.tare?.amount || 0)
      const deductions = material?.weightAndPrice?.deductions || []
      const { supplementalPriceEntry, defaultPriceEntry } = materialTypesDict[material.materialTypeId] || {}
      const priceEntry = supplementalPriceEntry || defaultPriceEntry
      const newPrice = priceEntry?.buyPrice?.price
      const newUnit = priceEntry?.weightUnits || defaultYardUm
      const newNetValue = getNetValue({
        gross,
        tare,
        price: Dinero({ ...newPrice }).toUnit(),
        deductions,
        fromUnit: defaultYardUm,
        toUnit: newUnit,
      })
      return {
        ...material,
        weightAndPrice: {
          ...(material?.weightAndPrice || {}),
          ...(newPrice
            ? {
                netPrice: newPrice,
                customerPreferredPrice: newPrice,
                um: newUnit,
                customerPreferredUm: newUnit,
                netValue: {
                  amount: roundTo(newNetValue * 10 ** DEFAULT_PRECISION),
                  currency: defaultCurrency,
                  precision: DEFAULT_PRECISION,
                },
              }
            : {}),
        },
      }
    })
    setInboundTicketMaterials(formattedMaterials)
  }, [materialTypesDict])

  return { isFetching, materialTypesData }
}

export default useUpdateScaleIBTPrices
